import React, { useEffect, useState } from 'react';
import { Treemap, Tooltip, PieChart, Pie, Cell } from 'recharts';
import './CryptoForm.css'; // Import CSS for styling
import Rocket from './rocket.svg'; // Import the rocket SVG

const Heatmap = ({ data }) => {
  const [colors, setColors] = useState({});
  const [view, setView] = useState('treemap'); // State to toggle between treemap and pie chart

  // Generate a random HSL color
  const getRandomColor = () => {
    const hue = Math.floor(Math.random() * 360);
    const saturation = Math.floor(Math.random() * 30) + 70; // Between 70% and 100%
    const lightness = Math.floor(Math.random() * 30) + 40; // Between 40% and 70%
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };

  // Assign colors to labels, using localStorage for persistence
  const assignColors = () => {
    const storedColors = JSON.parse(localStorage.getItem('heatmapColors')) || {};
    const newColors = { ...storedColors };

    data.labels.forEach((label) => {
      if (!newColors[label]) {
        newColors[label] = getRandomColor();
      }
    });

    localStorage.setItem('heatmapColors', JSON.stringify(newColors));
    return newColors;
  };

  useEffect(() => {
    const assignedColors = assignColors();
    setColors(assignedColors);
  }, [data.labels]);

  const treeMapData = data.labels.map((label, index) => ({
    name: label,
    value: parseFloat(data.datasets[0].data[index]),
    fill: colors[label],
  }));

  const pieData = data.labels.map((label, index) => ({
    name: label,
    value: parseFloat(data.datasets[0].data[index]),
    fill: colors[label],
  }));

  return (
    <div className="heatmap"> 
    <div className="toggle-buttons">
  <span
    onClick={() => setView('treemap')}
    className={`toggle-button ${view === 'treemap' ? 'active' : ''}`}
  >
    TreeMap
  </span>
  <span
    onClick={() => setView('piechart')}
    className={`toggle-button ${view === 'piechart' ? 'active' : ''}`}
  >
    PieChart
  </span>
</div>



    <div className="heatmap-container">
      
      
      {view === 'treemap' ? (
        treeMapData.length > 0 ? (
          <Treemap
            width={400}
            height={200}
            data={treeMapData}
            dataKey="value"
            content={<CustomContent />}
          >
            <Tooltip
              content={({ payload }) => {
                if (payload && payload.length) {
                  const { name, value } = payload[0].payload;
                  const percentage = (
                    (value /
                      data.datasets[0].data.reduce(
                        (acc, val) => acc + parseFloat(val),
                        0
                      )) *
                    100
                  ).toFixed(2);
                  return (
                    <div className="custom-tooltip">
                      <p>{`${name}: ${percentage}%`}</p>
                    </div>
                  );
                }
                return null;
              }}
            />
          </Treemap>
        ) : (
          <div className="no-data-container">
            <img src={Rocket} alt="No Data" className="no-data-icon" />
          </div>
        )
      ) : (
        <ResponsivePieChart pieData={pieData} />
      )}
    </div>
  </div> 
  );
};

const CustomContent = ({ depth, x, y, width, height, name, fill }) => {
  const fontSize = name ? Math.min(width / name.length, height / 2) : 12; // Adjust font size based on box size, default to 12 if name is undefined
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        className="heatmap-rect"
        style={{
          fill,
          stroke: '#000', // Set stroke color to black
          strokeWidth: 0 / (depth + 1e-10),
        }}
      />
      {width > 50 && height > 20 && name && (
        <text
          x={x + width / 2}
          y={y + height / 2}
          textAnchor="middle"
          dominantBaseline="middle"
          className="heatmap-text"
          style={{ fontSize: `${fontSize}px`, fontWeight: '300' }} // Apply adjusted font size and thin font weight
        >
          {name}
        </text>
      )}
    </g>
  );
};

const ResponsivePieChart = ({ pieData }) => {
  // State for dynamic chart size
  const [chartSize, setChartSize] = useState({ width: 500, height: 200, radius: 90 });

  // Update chart size based on screen width
  useEffect(() => {
    const updateChartSize = () => {
      if (window.innerWidth < 768) {
        setChartSize({ width: 900, height: 150, radius: 400 }); // Smaller size for mobile
      } else {
        setChartSize({ width: 500, height: 200, radius: 90 }); // Default size for desktop
      }
    };

    updateChartSize(); // Run on mount
    window.addEventListener('resize', updateChartSize);
    
    return () => window.removeEventListener('resize', updateChartSize);
  }, []);

  return (
    <PieChart width={chartSize.width} height={chartSize.height}>
      <Pie
        data={pieData}
        dataKey="value"
        nameKey="name"
        cx="50%"
        cy="50%"
        outerRadius={chartSize.radius}
        fill="#8884d8"
        label
      >
        {pieData.map((entry, index) => (
          <Cell key={`cell-${index}`} fill={entry.fill} stroke="none" /> // Remove white outline
        ))}
      </Pie>
      <Tooltip
        position={{ x: 0, y: 0 }} // Position tooltip directly over the section
        content={({ payload }) => {
          if (payload && payload.length) {
            const { name, value } = payload[0].payload;
            const percentage = (
              (value /
                pieData.reduce((acc, val) => acc + parseFloat(val.value), 0)) *
              100
            ).toFixed(2);
            return (
              <div className="custom-tooltip">
                <p>{`${name}: ${percentage}%`}</p>
              </div>
            );
          }
          return null;
        }}
      />
    </PieChart>
  );
};

export default Heatmap;
